import type {CalendarViewMode} from "~/components/beatPlanningCalendar/calendarToolbarBus";
import {CALENDAR_MAX_VISIBLE_STACKED_EVENTS} from "~/components/beatPlanningCalendar/calendarEventCountDisplay";

export type CalendarRowsFetchKind = "yearCounts" | "monthPreview" | "week" | "day" | "scheduleList";

export const CALENDAR_WEEK_ROW_LIMIT = 500;
export const CALENDAR_DAY_MODAL_PAGE_SIZE = 20;
export const CALENDAR_SCHEDULE_LIST_PAGE_SIZE = 100;
/** Preserve the bounded row request used by calendars before orchestration was introduced. */
export const LEGACY_DIRECT_CALENDAR_ROW_LIMIT = 9999;

export function resolveCalendarRowsFetchKind(calendarViewMode: CalendarViewMode | null): CalendarRowsFetchKind {
    if (calendarViewMode === "multiMonthYear") {
        return "yearCounts";
    }
    if (calendarViewMode === "dayGridMonth") {
        return "monthPreview";
    }
    if (calendarViewMode === "scheduleList") {
        return "scheduleList";
    }
    if (
        calendarViewMode === "timeGridWeek" ||
        calendarViewMode === "timeGridDay" ||
        calendarViewMode === "timeGridFourDay"
    ) {
        return "week";
    }
    return "monthPreview";
}

/**
 * List-fetch limits for Month/Week/Day/Schedule. Year uses count-by-column (no row limit).
 * Month caps at stacked chips × visible days; never near 9999.
 */
export function resolveCalendarRowsLimit({
    fetchKind,
    visibleRangeStart,
    visibleRangeEnd,
}: {
    fetchKind: CalendarRowsFetchKind;
    visibleRangeStart: Date | null;
    visibleRangeEnd: Date | null;
}): number {
    if (fetchKind === "yearCounts") {
        return 0;
    }
    if (fetchKind === "scheduleList") {
        return CALENDAR_SCHEDULE_LIST_PAGE_SIZE;
    }
    if (fetchKind === "day") {
        return CALENDAR_DAY_MODAL_PAGE_SIZE;
    }
    if (fetchKind === "week") {
        return CALENDAR_WEEK_ROW_LIMIT;
    }
    if (visibleRangeStart === null || visibleRangeEnd === null) {
        return CALENDAR_MAX_VISIBLE_STACKED_EVENTS * 42;
    }
    const msPerDay = 24 * 60 * 60 * 1000;
    const daySpan = Math.max(1, Math.ceil((visibleRangeEnd.getTime() - visibleRangeStart.getTime()) / msPerDay));
    return CALENDAR_MAX_VISIBLE_STACKED_EVENTS * daySpan;
}

export function resolveCalendarRowsLimitForDataSource(args: {
    calendarDataSource: "direct" | "orchestration";
    fetchKind: CalendarRowsFetchKind;
    visibleRangeStart: Date | null;
    visibleRangeEnd: Date | null;
}): number {
    if (args.calendarDataSource === "direct" && args.fetchKind !== "scheduleList") {
        return LEGACY_DIRECT_CALENDAR_ROW_LIMIT;
    }
    return resolveCalendarRowsLimit({
        fetchKind: args.fetchKind,
        visibleRangeStart: args.visibleRangeStart,
        visibleRangeEnd: args.visibleRangeEnd,
    });
}

export function dayCountsToYearStats(
    dayCounts: Array<{count: number; value: string | null}>,
): {
    eventDayKeys: Set<string>;
    monthEventTotals: Map<string, number>;
    monthTypeBars: Map<string, string>;
    yearViewDayStats: Map<string, {count: number; byType: Map<string, {label: string; count: number}>; statusLine: string}>;
} {
    const eventDayKeys = new Set<string>();
    const monthEventTotals = new Map<string, number>();
    const yearViewDayStats = new Map<
        string,
        {count: number; byType: Map<string, {label: string; count: number}>; statusLine: string}
    >();

    for (const row of dayCounts) {
        if (row.value === null || row.value.trim() === "") {
            continue;
        }
        const dayKey = row.value.slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(dayKey) !== true) {
            continue;
        }
        eventDayKeys.add(dayKey);
        yearViewDayStats.set(dayKey, {
            count: row.count,
            byType: new Map(),
            statusLine: `${row.count} ${row.count === 1 ? "event" : "events"}`,
        });
        const monthKey = dayKey.slice(0, 7);
        monthEventTotals.set(monthKey, (monthEventTotals.get(monthKey) ?? 0) + row.count);
    }

    return {
        eventDayKeys: eventDayKeys,
        monthEventTotals: monthEventTotals,
        monthTypeBars: new Map<string, string>(),
        yearViewDayStats: yearViewDayStats,
    };
}
